// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import {
	getArrayDiff,
	getObjectKeys,
	NullPartial,
	omitKeys,
	pickKeys,
	subsetTypeHasKey,
	ValueOf,
} from "@hex-insights/core";
import { PermissionGroupCreateInput, PermissionGroupDetailQuery, PermissionGroupUpdateInput } from "../../../Utilities";
import * as PermissionGroupFormValues from "./form-values";

/**
 * Converts the given `formValues` for the create form to a GraphQL create input for the PermissionGroup model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInput<
	K extends keyof PermissionGroupFormValues.Create = keyof PermissionGroupFormValues.Create,
>(formValues: PermissionGroupFormValues.CreateSubset<K>): PermissionGroupCreateInput {
	const input: NullPartial<PermissionGroupCreateInput> = { ...formValues };
	return input as PermissionGroupCreateInput;
}

/**
 * Converts the given `formValues` for the detail form to a GraphQL update input for the PermissionGroup model.
 *
 * @param formValues The form values to convert. Generally only the changed form values.
 * @param initialFormValues The initial values of the form.
 */
export function toGQLUpdateInput<
	K extends keyof PermissionGroupFormValues.Detail = keyof PermissionGroupFormValues.Detail,
>(
	formValues: Partial<PermissionGroupFormValues.DetailSubset<K>>,
	initialFormValues: PermissionGroupFormValues.DetailSubset<K>,
): PermissionGroupUpdateInput {
	const inputFormValues = omitKeys(formValues, ["permissionIDs", "userIDs"]);
	const input: PermissionGroupUpdateInput = { ...inputFormValues };

	if (
		subsetTypeHasKey(formValues, "permissionIDs") &&
		subsetTypeHasKey(initialFormValues, "permissionIDs") &&
		formValues.permissionIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.permissionIDs, formValues.permissionIDs);
		input.addPermissionIDs = added;
		input.removePermissionIDs = removed;
	}

	if (
		subsetTypeHasKey(formValues, "userIDs") &&
		subsetTypeHasKey(initialFormValues, "userIDs") &&
		formValues.userIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.userIDs, formValues.userIDs);
		input.addUserIDs = added;
		input.removeUserIDs = removed;
	}

	return input;
}

export type PermissionGroupFormKeyMap = {
	name: "name";
	description: "description";
	permissionIDs: "permissions";
	userIDs: "users";
};

export type ToFormValuesArgs<
	K extends keyof PermissionGroupFormValues.Detail = keyof PermissionGroupFormValues.Detail,
> = {
	permissionGroup: Pick<PermissionGroupDetailQuery["permissionGroup"], ValueOf<Pick<PermissionGroupFormKeyMap, K>>>;
	fields?: { [Key in K]: true };
};

/**
 * Converts a PermissionGroup instance to its corresponding detail form values.
 *
 * @param permissionGroup The instance to convert.
 * @param fields The subset of fields to include.
 */
export function toFormValues<
	K extends keyof PermissionGroupFormValues.Detail = keyof PermissionGroupFormValues.Detail,
>({ permissionGroup, fields }: ToFormValuesArgs<K>): PermissionGroupFormValues.DetailSubset<K> {
	const formValues = {
		name: subsetTypeHasKey(permissionGroup, "name") ? permissionGroup.name : "",
		description: subsetTypeHasKey(permissionGroup, "description") ? permissionGroup.description : "",
		permissionIDs: subsetTypeHasKey(permissionGroup, "permissions") ? permissionGroup.permissions.map((e) => e.id) : [],
		userIDs: subsetTypeHasKey(permissionGroup, "users") ? permissionGroup.users.map((e) => e.id) : [],
	};
	return fields ? pickKeys(formValues, getObjectKeys(fields)) : formValues;
}
